import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight, Eye, MoreHorizontal, PanelLeft, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import Tooltip from 'components/ui/tooltip';
import LogoMark from 'components/global/LogoMark';
import { HeaderProfile } from 'components/global/HeaderProfile';
import { HelpTourButton } from 'components/global/HelpTourButton';
import { StatusBadge } from 'components/ui/status-badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { LifecycleStrip } from 'components/workspace/LifecycleStrip';
import { LiveIndicator, useWorkspaceLiveState } from 'components/workspace/WorkspaceLive';
import { lifecycleSteps } from 'components/workspace/lifecycle';
import { useAttemptCount } from 'components/workspace/useAttemptCount';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { cn } from 'lib/utils';
import { useTestConfigSidebar } from './TestConfigSidebarContext';
import { useTestLifecycle } from './TestLifecycleContext';
import { TestStatusBadge } from './TestStatusBadge';

/** "118 topshirdi · 9 kishi hozir ishlamoqda" for a test that has candidates. */
function RunTotals({ submitted, inProgress }: { submitted: number; inProgress?: number }) {
  const { t } = useTranslation();
  return (
    <span className="truncate text-[13px] text-muted-foreground">
      <span className="font-semibold tabular-nums text-foreground">{submitted}</span> {t('workspace_run_submitted')}
      {inProgress != null && inProgress > 0 && <> · {t('workspace_run_in_progress', { count: inProgress })}</>}
    </span>
  );
}

/**
 * The desktop workspace chrome (≥768px): a 48px header on the page background —
 * way back, breadcrumb, status, Preview, the lifecycle action, more, account —
 * over the white lifecycle strip.
 */
export const TestHeader: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const { toggle: toggleRail } = useTestConfigSidebar();
  const lc = useTestLifecycle();
  const live = useWorkspaceLiveState();
  const canViewResults = usePermission(PERMISSIONS.TESTS_VIEW_RESULTS);

  const { phase, readiness } = lc;
  const running = phase !== 'draft';
  // The strip's run totals: only when no live page owns the slot, and only for
  // people who may see results (the results API scopes everyone else to their
  // own attempts, which would read as a wrong total).
  const wantTotals = running && !live && canViewResults;
  const submitted = useAttemptCount(lc.testId, 'completed', wantTotals);
  const inProgress = useAttemptCount(lc.testId, 'in_progress', wantTotals && phase === 'active');

  let aside: React.ReactNode = null;
  if (live) {
    aside = <LiveIndicator connected={live.connected} />;
  } else if (phase === 'draft' && lc.checksRemaining > 0) {
    aside = (
      <StatusBadge tone="warning" className="truncate">
        {t('checks_remaining', { count: lc.checksRemaining })}
      </StatusBadge>
    );
  } else if (wantTotals && submitted != null) {
    aside = <RunTotals submitted={submitted} inProgress={inProgress} />;
  }

  return (
    <div className={cn('shrink-0', className)}>
      <header className="flex h-12 items-center gap-2.5 border-b border-border bg-background px-4">
        {/* Below lg the rail is a drawer; this opens it. */}
        <button
          type="button"
          onClick={toggleRail}
          aria-label={t('expand_sidebar')}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:hidden"
        >
          <PanelLeft className="h-4 w-4" />
        </button>
        {/* Focus mode hides the shell nav, so this is the always-visible way out. */}
        <Tooltip content={t('back_to_tests')} side="bottom">
          <Link
            to="/app/tests"
            aria-label={t('back_to_tests')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-foreground/80 transition-colors hover:border-foreground/20 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Tooltip>
        <LogoMark size={26} className="rounded-md" />
        <nav aria-label={t('breadcrumb')} className="flex min-w-0 items-center gap-1.5 text-[13px]">
          <Link to="/app/tests" className="shrink-0 text-muted-foreground hover:text-foreground hover:underline">
            {t('nav.tests')}
          </Link>
          <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
          <span aria-current="page" className="truncate font-semibold text-foreground">
            {lc.title}
          </span>
        </nav>
        <TestStatusBadge status={lc.status} className="shrink-0" />

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button variant="outline" size="xs" className="gap-1.5" asChild>
            <Link to={`/app/tests/preview/${lc.testId}`}>
              <Eye className="h-[15px] w-[15px]" />
              {t('preview')}
            </Link>
          </Button>
          {phase === 'draft' && (
            <Button size="xs" disabled={!readiness.allReady || lc.activating} onClick={lc.requestActivate}>
              {t('activate_test')}
            </Button>
          )}
          {phase === 'active' && (
            <Button variant="outline" size="xs" disabled={lc.finishing} onClick={lc.finish}>
              {t('finish_test')}
            </Button>
          )}
          {phase === 'finished' && (
            <Button size="xs" disabled={lc.activating} onClick={lc.requestActivate}>
              {t('reactivate_test')}
            </Button>
          )}
          {/* The destructive action sits in a menu so it can't be hit while
              reaching for the lifecycle button. */}
          {lc.canDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="xs" className="w-8 px-0" aria-label={t('more_options')}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="gap-2 text-destructive focus:text-destructive" onSelect={lc.requestDelete}>
                  <Trash2 className="h-4 w-4" />
                  {t('delete_assessment')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Focus mode hides the shell header, so the help tour and account menu live here. */}
          <HelpTourButton />
          <span className="ml-1 flex">
            <HeaderProfile />
          </span>
        </div>
      </header>

      <LifecycleStrip steps={lifecycleSteps(phase, readiness.allReady)} aside={aside} />
    </div>
  );
};

export default TestHeader;
